import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Download, FileSpreadsheet } from "lucide-react";

export default function MutasiAkun() {
  const { id } = useParams();
  const { user } = useAuth();
  const back = user?.workspace_type === "pribadi" ? "/pribadi/akun" : "/kas-bank/akun";
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [d, setD] = useState(null);
  const load = async () => {
    try { const r = await api.get(`/akun/${id}/mutasi`, { params: { start: start || undefined, end: end || undefined } }); setD(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (fmt) => {
    const url = new URL(`${API_BASE}/akun/${id}/mutasi/export`);
    url.searchParams.set("fmt", fmt);
    if (start) url.searchParams.set("start", start);
    if (end) url.searchParams.set("end", end);
    const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("khn_token")}` } });
    if (!res.ok) { toast.error("Gagal mengunduh"); return; }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `mutasi-${d?.akun?.nama || "akun"}.${fmt}`; a.click();
  };

  return (
    <>
      <PageHeader title={`Mutasi ${d?.akun?.nama || "Akun"}`} subtitle={d?.akun?.no_rekening ? `No. ${d.akun.no_rekening} — cocokkan dengan rekening koran / uang di kas.` : "Cocokkan dengan rekening koran / uang di kas."} actions={
        <div className="flex gap-2">
          <Link to={back}><Button variant="outline" className="rounded-sm" data-testid="mutasi-back"><ArrowLeft size={14} className="mr-1"/>Kembali</Button></Link>
          <Button variant="outline" className="rounded-sm" onClick={() => download("pdf")} data-testid="mutasi-pdf"><Download size={14} className="mr-1"/>PDF</Button>
          <Button variant="outline" className="rounded-sm" onClick={() => download("xlsx")} data-testid="mutasi-xlsx"><FileSpreadsheet size={14} className="mr-1"/>Excel</Button>
        </div>
      }/>
      <div className="p-4 md:p-8 space-y-4">
        <div className="card-flat p-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1.5"><Label>Dari</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="mutasi-start"/></div>
          <div className="space-y-1.5"><Label>Sampai</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="mutasi-end"/></div>
          <Button onClick={load} className="brand-btn rounded-sm" data-testid="mutasi-filter">Tampilkan</Button>
        </div>
        {d && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="card-flat p-4"><div className="text-[11px] uppercase tracking-widest text-slate-500">Saldo Awal Periode</div><div className="font-mono text-lg mt-1" data-testid="mutasi-saldo-awal">{rupiah(d.saldo_awal_periode)}</div></div>
            <div className="card-flat p-4"><div className="text-[11px] uppercase tracking-widest text-slate-500">Total Masuk</div><div className="font-mono text-lg mt-1 text-emerald-700">{rupiah(d.total_masuk)}</div></div>
            <div className="card-flat p-4"><div className="text-[11px] uppercase tracking-widest text-slate-500">Total Keluar</div><div className="font-mono text-lg mt-1 text-rose-700">{rupiah(d.total_keluar)}</div></div>
            <div className="card-flat p-4"><div className="text-[11px] uppercase tracking-widest text-slate-500">Saldo Akhir</div><div className={`font-mono text-lg mt-1 ${d.saldo_akhir < 0 ? "text-rose-700" : "text-blue-700"}`} data-testid="mutasi-saldo-akhir">{rupiah(d.saldo_akhir)}</div></div>
          </div>
        )}
        <div className="card-flat overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]" data-testid="mutasi-table">
            <thead><tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500">
              <th className="p-3">Tanggal</th><th className="p-3">Jenis</th><th className="p-3">Keterangan</th><th className="p-3 text-right">Masuk</th><th className="p-3 text-right">Keluar</th><th className="p-3 text-right">Saldo</th>
            </tr></thead>
            <tbody>
              {d && <tr className="border-b bg-slate-50/50 text-slate-500"><td className="p-3" colSpan="5">Saldo awal periode</td><td className="p-3 text-right font-mono">{rupiah(d.saldo_awal_periode)}</td></tr>}
              {d?.rows?.length === 0 && <tr><td colSpan="6" className="text-center py-10 text-slate-400">Belum ada mutasi pada periode ini.</td></tr>}
              {(d?.rows || []).map((r, i) => (
                <tr key={`${r.ref}-${i}`} className="border-b hover:bg-slate-50">
                  <td className="p-3 whitespace-nowrap">{r.tanggal}</td>
                  <td className="p-3"><span className={`text-[11px] px-2 py-0.5 rounded-sm ${r.masuk ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{r.jenis}</span></td>
                  <td className="p-3 text-slate-700">{r.keterangan}</td>
                  <td className="p-3 text-right font-mono text-emerald-700">{r.masuk ? rupiah(r.masuk) : "-"}</td>
                  <td className="p-3 text-right font-mono text-rose-700">{r.keluar ? rupiah(r.keluar) : "-"}</td>
                  <td className="p-3 text-right font-mono">{rupiah(r.saldo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
